export type RatingClub = {id: number; name: string};
export type RatingTournament = {id: number; season: string; name: string; year: number; kind: "championship" | "cup"; pending: boolean; points: Record<string, number>; note: string};
export type RatingMatch = {id: number; tournamentId: number; home: number; away: number; hg: number; ag: number; date: string};
export type RatingData = {events: {id:number;season:string;eligibleGames:number}[]; notes:string[]; clubs: RatingClub[]; tournaments: RatingTournament[]; matches: RatingMatch[]; exclusions: {season: string; reason: string; count: number}[]; totalMatches: number; latestDate: string};
export type AchievementSettings = {year: number; years: number; championship: number; cup: number; excluded: number[]};
export function achievements(data: RatingData, settings: AchievementSettings) {
  const tours = data.tournaments.filter(t => t.year <= settings.year && t.year > settings.year-settings.years && !settings.excluded.includes(t.id));
  const ids = new Set(tours.flatMap(t=>Object.keys(t.points).map(Number)));
  const rows = data.clubs.filter(c=>ids.has(c.id)).map(club=>{
    const annual = Array.from({length:settings.years},(_,i)=>{
      const year=settings.year-i, selected=tours.filter(t=>t.year===year);
      const average=(kind:RatingTournament["kind"])=>{
        const list=selected.filter(t=>t.kind===kind);
        return list.length ? list.reduce((sum,t)=>sum+(t.points[club.id]??0),0)/list.length : 0;
      };
      const championship=average("championship")*settings.championship/100;
      const cup=average("cup")*settings.cup/60;
      const weight=(settings.years-i)/settings.years;
      return {year, championship, cup, weight, contribution:(championship+cup)*weight};
    });
    return {...club, score:annual.reduce((sum,a)=>sum+a.contribution,0), annual};
  }).sort((a,b)=>b.score-a.score || a.name.localeCompare(b.name,"ru"));
  let rank=0;
  return rows.map((row,i)=>{
    if(i===0 || Math.abs(row.score-rows[i-1].score)>1e-9)rank=i+1;
    return {...row,rank};
  });
}
export type EloSettings={date: string; k: number; scale: number; excluded:number[]};
export type EloEntry={match:RatingMatch;before:number;opponentBefore:number;expected:number;delta:number;after:number};
export function elo(data: RatingData, settings:EloSettings) {
  const matches=data.matches.filter(m=>m.date<=settings.date&&!settings.excluded.includes(m.tournamentId)).sort((a,b)=>a.date.localeCompare(b.date)||a.id-b.id);
  const rating=new Map<number,number>(),counts=new Map<number,number>(),last=new Map<number,string>(),history=new Map<number,EloEntry[]>(),old=new Map<number,number>();
  const parent=new Map<number,number>();
  const root=(id:number):number=>{const p=parent.get(id)??id;if(p===id)return id;const r=root(p);parent.set(id,r);return r;};
  const cutoff=new Date(new Date(settings.date+"T00:00:00Z").getTime()-30*86400000).toISOString().slice(0,10);
  for(let start=0;start<matches.length;){
    let end=start+1;while(end<matches.length&&matches[end].date===matches[start].date)end++;
    const changes=new Map<number,number>();
    for(const m of matches.slice(start,end)){
      const a=rating.get(m.home)??1500,b=rating.get(m.away)??1500;
      const expected=1/(1+10**((b-a)/settings.scale));
      const score=m.hg===m.ag?.5:m.hg>m.ag?1:0,delta=settings.k*(score-expected);
      parent.set(root(m.home),root(m.away));
      for(const [id,d,before,opponentBefore,e] of [[m.home,delta,a,b,expected],[m.away,-delta,b,a,1-expected]] as const){
        changes.set(id,(changes.get(id)??0)+d);counts.set(id,(counts.get(id)??0)+1);last.set(id,m.date);
        const list=history.get(id)??[];list.push({match:m,before,opponentBefore,expected:e,delta:d,after:0});history.set(id,list);
      }
    }
    for(const [id,change] of changes){const value=(rating.get(id)??1500)+change;rating.set(id,value);
      // При пакете дня after показывает итог дня, а не фиктивный порядок матчей.
      for(const e of history.get(id)??[])if(e.match.date===matches[start].date)e.after=value;
      if(matches[start].date<cutoff)old.set(id,value);
    }
    start=end;
  }
  const groups=[...new Set([...rating.keys()].map(root))].sort((a,b)=>a-b);
  const rows=data.clubs.filter(c=>rating.has(c.id)).map(c=>({...c,score:rating.get(c.id)!,games:counts.get(c.id)!,last:last.get(c.id)!,change:rating.get(c.id)!-(old.get(c.id)??1500),history:history.get(c.id)??[],group:groups.indexOf(root(c.id))+1,
    inactive:(new Date(settings.date+"T00:00:00Z").getTime()-new Date(last.get(c.id)!+"T00:00:00Z").getTime())>365*86400000
  })).sort((a,b)=>a.group-b.group||Number(a.inactive)-Number(b.inactive)||b.score-a.score||a.name.localeCompare(b.name,"ru"));
  let rank=0;
  return {games:matches.length,groups:groups.length,rows:rows.map((r,i)=>{
    const first=i===0||r.group!==rows[i-1].group||r.inactive!==rows[i-1].inactive;
    if(first)rank=1;else if(Math.abs(r.score-rows[i-1].score)>1e-9)rank=rows.slice(0,i).filter(x=>x.group===r.group&&x.inactive===r.inactive).length+1;
    return {...r,rank};
  })};
}
